import { DueDateStatus, Instruction, InstructionType, IpRightStatus, Renewal, RenewalState } from "src/renewal/interfaces/renewal_types"
import { Fee, MaintenanceAction, MaintenanceActionPhase, PcCostCenter, PcCostCenterLink, PcIpRight, PcOwnership, useDennemeyer } from "../DennemeyerProvider"
import { Member } from "src/patents/patents"
import _ from "lodash"
import { post_instructions } from "../dennemeyer_backend"

export function getOwnerAndCostCenters(ipRight: PcIpRight, ownershipsByIpRight: Record<number, PcOwnership[]>, costCentersByIpRightId: Record<number, (PcCostCenter & PcCostCenterLink)[]>) {
  if (!ipRight) {
    return { owner: undefined, costCenters: {} }
  }
  const ccs = costCentersByIpRightId[ipRight.ipRightId]
  const costCenters = _(ccs)
    .map(cc => [cc.costCenterId, cc.percentage])
    .fromPairs()
    .value()
  const owner = ownershipsByIpRight[ipRight.ipRightId]?.[0]?.ownerId

  return { owner, costCenters }
}

export function dmIpRightToIpRight(ipRight: PcIpRight, member: Member, owner: number, costCenters: Record<number, number>) {
  const status: IpRightStatus =
    member?.familyMemberStatus === 'stopped'
      ? 'Expired'
      : // : dmIpRight === undefined
        // ? 'Processing'
        'Ready'
  return {
    id: ipRight.ipRightId + '',
    clientReference: member?.internalReference,
    clientUniqueId: '' + ipRight.ipRightId,
    publicationNumber: member?.publicationNumber,
    grantNumber: member?.patentNumber,
    status,
    costCenters,
    owner,
    familyMemberId: ipRight.familyMemberId,
  }
}

export function maintenanceActionToRenewal(
  maintenanceAction: MaintenanceAction,
  calculateDueDates: (dueDate: string) => { status: DueDateStatus; instructionDueDate: string },
  instructionByRenewalId: Record<string, Instruction>,
  ipRightId?: string,
): Renewal {
  const { status, instructionDueDate } = calculateDueDates(maintenanceAction.DueDate)
  let fees: Fee[] = maintenanceAction.FeesActual ?? []
  if (fees.length === 0) {
    fees = maintenanceAction.FeesEstimated ?? []
  }
  // console.log({fees, maintenanceAction})
  const isAutoPay = maintenanceAction.PermanentOrder
  const instruction = instructionByRenewalId[maintenanceAction.DennemeyerId]

  const debitNotes = maintenanceAction?.FeesActual?.map(fee => fee.DNNumber + '') ?? []
  const renewalNotices = maintenanceAction?.FeesEstimated?.map(fee => fee.RNNumber + '') ?? []
  const certificates = []
  const documents = { debitNotes, renewalNotices, certificates }

  return {
    id: maintenanceAction.DennemeyerId,
    ipRightId: maintenanceAction?.IpRightInfo?.UniqueCaseKey ?? ipRightId,
    state: phaseToState(maintenanceAction.Phase, instruction),
    //   created: maintenanceAction.Created,
    //   updated: maintenanceAction.Updated,
    instructionDueDate,
    instruction,
    feeDueDate: maintenanceAction.DueDate,
    fee: _(fees)
      .map(f => [f.Currency, f.TotalFee])
      .fromPairs()
      .value(),
    isAutoPay,
    annuity: maintenanceAction.Annuity ? maintenanceAction.Annuity + '' : undefined,
    documents,
  } as Renewal
}

function phaseToState(phase: MaintenanceActionPhase, instruction?: Instruction): RenewalState {
  if (phase === 'Auto-Pay in Progress' || phase === 'Upcoming' || phase === 'Waiting For Instruction') {
    if (instruction !== undefined) return 'Instructed'
    return 'Open'
  } else if (phase === 'Instructed / In Progress' || phase === 'Closed') {
    return 'Paid'
  } else {
    return 'Cancelled'
  }
}

export function toCommonInstructionType(i): InstructionType {
  switch (i) {
    case 'Pay':
      return 'Pay'
    case 'Cancel':
      return 'Cancel'
    default:
      return undefined
  }
}

export function useInstruct({renewalById}: {renewalById: Record<string, Renewal>}) {
  const { instructionByDennemeyerId, postInstruction, deleteInstruction, invalidateIpRights } = useDennemeyer()

  async function instruct(renewalId: string, instruction: Instruction | undefined) {
    const inst = instructionByDennemeyerId[renewalId]
    if (inst !== undefined) {
      await deleteInstruction(inst)
    }
    const renewal = renewalById[renewalId]
    if (instruction !== undefined && renewal !== undefined) {
      // console.log({instruction})
      await postInstruction({
        instruction: instruction.instruction,
        commentId: instruction.commentId,
        created: instruction.created,
        dennemeyerId: renewalId,
        ipRightId: +renewal.ipRightId,
        // status: 'Instructed',
      })

      if ((renewal.isAutoPay && instruction.instruction === 'Cancel') || (!renewal.isAutoPay && instruction.instruction === 'Pay')) {
        await post_instructions([{ DennemeyerId: renewalId, Instruction: instruction.instruction }])
      }
    }
    invalidateIpRights()
    return Promise.resolve()
  }

  // TODO: use bulk instruction capabilities of DM
  async function bulkInstruct(instructions: Record<string, Instruction>) {
    await Promise.all(
      _(instructions)
        .toPairs()
        .map(([renewalId, instruction]) => {
          instruct(renewalId, instruction)
        })
        .value(),
    )
  }

  return { instruct, bulkInstruct }
}